import React, { useEffect, useRef } from 'react';
import { Card, CardContent, Typography, Button, Box } from '@mui/material';
import { useLanguage } from '../../../context/LanguageContext';
import { useBrand } from '../../../context/BrandContext';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface StoreMapProps {
  latitude: string | number;
  longitude: string | number;
  storeName?: string;
  address?: string;
}

const StoreMap: React.FC<StoreMapProps> = ({ latitude, longitude, storeName, address }) => {
  const { t } = useLanguage();
  const { brand } = useBrand();
  const mapRef = useRef<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  const lat = typeof latitude === 'string' ? parseFloat(latitude) : latitude;
  const lng = typeof longitude === 'string' ? parseFloat(longitude) : longitude;

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = L.map(mapContainerRef.current).setView([lat, lng], 15);
    
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(map);

    const marker = L.marker([lat, lng]).addTo(map);
    
    if (storeName || address) {
      const popupContent = `
        ${storeName ? `<strong>${storeName}</strong><br/>` : ''}
        ${address ? `${address}` : ''}
      `;
      marker.bindPopup(popupContent);
    }

    map.on('dragstart', () => {
      isDraggingRef.current = true;
    });

    map.on('dragend', () => {
      setTimeout(() => {
        isDraggingRef.current = false;
      }, 100);
    });

    marker.on('click', () => {
      handleGetDirections();
    });

    mapRef.current = map;

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng]);

  const handleGetDirections = () => {
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(mapsUrl, '_blank');
  };

  const handleMapClick = () => {
    if (!isDraggingRef.current) {
      handleGetDirections();
    }
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <i className="bx bx-map-pin" style={{ fontSize: 24, color: brand.primaryColor }} />
            {t('pages.myStore.location')}
          </Typography>
          <Button
            variant="text"
            size="small"
            onClick={handleGetDirections}
            sx={{
              color: brand.primaryColor,
              whiteSpace: 'nowrap',
              '&:hover': {
                backgroundColor: `${brand.primaryColor}15`,
              },
            }}
          >
            {t('pages.myStore.getDirections')}
          </Button>
        </Box>
        
        <div 
          ref={mapContainerRef}
          style={{ 
            width: '100%',
            flexGrow: 1,
            minHeight: '300px',
            borderRadius: '4px',
            cursor: 'grab'
          }}
          onClick={handleMapClick}
        />
      </CardContent>
    </Card>
  );
};

export default StoreMap;
